"use client";

import { createContext, useContext, useState, useCallback, useRef, type ReactNode, type RefObject } from "react";

interface LoaderContextType {
  /** True once the loader has completed this page load */
  hasLoaderBeenShown: boolean;
  /** True while the loader is actively showing */
  isLoaderActive: boolean;
  /** Called when loader animation completes */
  markLoaderAsShown: () => void;
  /** Returns delay (350 if loader just completed, 0 otherwise) and consumes it */
  consumeLoaderDelay: () => number;
  /** Register hero ref for coordinated animation */
  registerHeroRef: (ref: RefObject<HTMLDivElement | null>) => void;
  /** Get registered hero ref (mutable ref for immediate access) */
  heroRefHolder: React.MutableRefObject<RefObject<HTMLDivElement | null> | null>;
}

const LoaderContext = createContext<LoaderContextType | null>(null);

// Module-level flags - reset on every full page refresh, persist across client navigations
let loaderShownThisPageLoad = false;
let loaderDelayConsumed = false;

export function LoaderProvider({ children }: { children: ReactNode }) {
  const initialLoaderShown = useRef(loaderShownThisPageLoad);

  const [hasLoaderBeenShown, setHasLoaderBeenShown] = useState(initialLoaderShown.current);
  const [isLoaderActive, setIsLoaderActive] = useState(!initialLoaderShown.current);
  // Use a mutable ref so it's immediately available without re-render
  const heroRefHolder = useRef<RefObject<HTMLDivElement | null> | null>(null);

  const markLoaderAsShown = useCallback(() => {
    loaderShownThisPageLoad = true;
    loaderDelayConsumed = false; // Reset so first page after loader gets delay
    setHasLoaderBeenShown(true);
    setIsLoaderActive(false);
  }, []);

  // Returns delay and marks it as consumed (subsequent calls return 0)
  const consumeLoaderDelay = useCallback(() => {
    if (!loaderDelayConsumed && loaderShownThisPageLoad) {
      loaderDelayConsumed = true;
      return 350;
    }
    return 0;
  }, []);

  const registerHeroRef = useCallback((ref: RefObject<HTMLDivElement | null>) => {
    heroRefHolder.current = ref;
  }, []);

  return (
    <LoaderContext.Provider value={{
      hasLoaderBeenShown,
      isLoaderActive,
      markLoaderAsShown,
      consumeLoaderDelay,
      registerHeroRef,
      heroRefHolder,
    }}>
      {children}
    </LoaderContext.Provider>
  );
}

export function useLoaderState() {
  const context = useContext(LoaderContext);
  if (!context) {
    throw new Error("useLoaderState must be used within a LoaderProvider");
  }
  return context;
}
